import React from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Link,
  Image,
  Divider,
  Stack,
  Icon,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import {
  FaLinkedin,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaCheckCircle,
} from "react-icons/fa";
import { motion } from "framer-motion";
import logo from "../assets/logo.png";
import IndiaFlag from "../assets/india.jpg";
import USAFlag from "../assets/us.jpg";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionListItem = motion(ListItem);

export default function Footer() {
  const aboutSubMenu = [
    { name: "Overview", id: "overview" },
    { name: "Why HoriSun", id: "why" },
    { name: "Our Processes", id: "processes" },
  ];

  const { pathname } = useLocation();

  const handleScroll = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      bgGradient="linear(to-b, #F9FAFB, #E8F5E9)"
      color="#305D24"
      width="100%"
      mt={10}
      fontSize="sm"
      boxShadow="xl"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        wrap="wrap"
        gap={10}
        px={{ base: 6, md: 12 }}
        py={6}
        maxW="100%"
        mx="auto"
      >
        {/* Left Section - Logo + Contact */}
        <MotionVStack
          align="flex-start"
          spacing={2}
          flex="1"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link as={RouterLink} to="/" _hover={{ textDecoration: "none" }}>
            <Image
              src={logo}
              alt="HoriSun Logo"
              h={{ base: "80px", md: "100px" }}
              objectFit="contain"
              borderRadius="md"
              transition="all 0.3s ease"
              _hover={{ transform: "scale(1.05)" }}
            />
          </Link>

          <HStack align="start" spacing={3}>
            <Icon as={FaMapMarkerAlt} color="#66B649" mt={1} />
            <Text fontWeight="medium">
              D-202, 4th Floor, Phase 8B,
              <br />
              Industrial Area, Mohali-140308, India
            </Text>
          </HStack>

          <HStack spacing={3}>
            <Icon as={FaEnvelope} color="#66B649" />
            <Text fontWeight="medium">info@horisunrenewables.com</Text>
          </HStack>

          <VStack align="start" spacing={2}>
            <HStack spacing={3}>
              <Icon as={FaPhone} color="#66B649" />
              <Text fontWeight="medium">+91-78885-15459</Text>
              <Image
                src={IndiaFlag}
                alt="India"
                boxSize="18px"
                borderRadius="md"
              />
            </HStack>
            <HStack spacing={3}>
              <Icon as={FaPhone} color="#66B649" />
              <Text fontWeight="medium">+1-903-329-6285</Text>
              <Image src={USAFlag} alt="USA" boxSize="18px" borderRadius="md" />
            </HStack>
          </VStack>
        </MotionVStack>

        {/* Middle Section - Quick Links */}
        <Flex
          flex="2"
          justify="space-between"
          wrap="wrap"
          gap={8}
          fontSize="sm"
        >
          {[
            {
              title: "About Us",
              items: aboutSubMenu.map((item) => ({
                name: item.name,
                onClick: (e) => {
                  e.preventDefault();
                  if (pathname !== "/about") {
                    window.location.href = `/about#${item.id}`;
                  } else {
                    handleScroll(item.id);
                  }
                },
              })),
            },
            {
              title: "Services",
              items: [
                { name: "Design & Engineering", link: "/services/design" },
                {
                  name: "Feasibility Study",
                  link: "/services/feasibility-study",
                },
                {
                  name: "EPC Management (EPCM)",
                  link: "/services/epc-management",
                },
                {
                  name: "MW-Scale Project Management Consultancy",
                  link: "/services/consultancy",
                },
                {
                  name: "Consulting & Compliance Services",
                  link: "/services/advisory",
                },
                {
                  name: "Supply Chain Management",
                  link: "/services/supply-chain",
                },
              ],
            },
            {
              title: "Resources",
              items: [
                { name: "Careers", link: "/careers" },
                { name: "Contact Us", link: "/contact" },
              ],
            },
          ].map((section, idx) => (
            <MotionVStack
              key={idx}
              align="flex-start"
              spacing={3}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 * idx, duration: 0.6 }}
            >
              <Text
                fontWeight="extrabold"
                bgGradient="linear(to-r, #66B649, #F6A11A)"
                bgClip="text"
                fontSize="md"
                letterSpacing="wide"
              >
                {section.title}
              </Text>
              <List spacing={2}>
                {section.items.map((item, i) => (
                  <MotionListItem
                    key={i}
                    display="flex"
                    alignItems="center"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <ListIcon as={FaCheckCircle} color="#66B649" />
                    <Link
                      as={RouterLink}
                      to={item.link || "#"}
                      onClick={item.onClick}
                      fontWeight="medium"
                      _hover={{
                        color: "#66B649",
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      }}
                    >
                      {item.name}
                    </Link>
                  </MotionListItem>
                ))}
              </List>
            </MotionVStack>
          ))}
        </Flex>
      </Flex>

      {/* Bottom Section (Moved Above Divider) */}
      <MotionBox
     /* reduced padding */

        px={{ base: 4, md: 10 }}
        pb={0}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Flex
          justify="space-between"
          align="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: 2, md: 1 }}
          color="#305D24"
        >
          <Text
            fontSize="sm"
            fontWeight="medium"
            textAlign="center"
            lineHeight="shorter"
          >
            © {new Date().getFullYear()} <b>HoriSun</b>. All rights reserved.
          </Text>

          <HStack spacing={{ base: 4, md: 5 }} fontSize="sm">
            <Link
              as={RouterLink}
              to="/"
              _hover={{
                color: "#66B649",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
              }}
              fontWeight="medium"
            >
              Privacy Policy
            </Link>
            <Link
              as={RouterLink}
              to="/"
              _hover={{
                color: "#66B649",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
              }}
              fontWeight="medium"
            >
              Terms & Conditions
            </Link>
          </HStack>

          <HStack spacing={2}>
            <Link
              href="https://in.linkedin.com/company/renewable-energy-solutions1"
              isExternal
              _hover={{ transform: "scale(1.08)" }}
              transition="all 0.2s ease"
            >
              <Icon
                as={FaLinkedin}
                boxSize={8}
                color="#0A66C2"
                _hover={{ color: "#005582" }}
                transition="all 0.2s ease"
              />
            </Link>
          </HStack>
        </Flex>
      </MotionBox>

      <Divider mt={4} borderColor="#E4EDE4" />
    </Box>
  );
}
